// src/components/UltraCustomerPopup.js
import React, { useState, useEffect } from "react";
import { CURRENT_ACTIVATION_FEE } from "../config/membershipConfig";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  Grid,
  Chip,
  Fade,
  Zoom,
} from "@mui/material";
import {
  Close,
  Star,
  TrendingUp,
  CardGiftcard,
  People,
  LocalOffer,
  Verified,
  RocketLaunch,
  Diamond,
  EmojiEvents,
} from "@mui/icons-material";

const UltraCustomerPopup = ({ open, onClose, onPurchase, userProfile }) => {
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    if (open) {
      // Delay content animation for smooth entrance
      const timer = setTimeout(() => setShowContent(true), 200);
      return () => clearTimeout(timer);
    } else {
      setShowContent(false);
    }
  }, [open]);

  const benefits = [
    {
      icon: <TrendingUp sx={{ fontSize: 32 }} />,
      title: "Earn Referral Points",
      description:
        "Earn up to 100% on direct referrals (L1) and more across 10 levels on profit margin",
      color: "#1E40AF",
    },
    {
      icon: <CardGiftcard sx={{ fontSize: 32 }} />,
      title: `₹${CURRENT_ACTIVATION_FEE} Instant Cashback`,
      description: "Receive cashback immediately on your first package purchase",
      color: "#00BFFF",
    },
    {
      icon: <People sx={{ fontSize: 32 }} />,
      title: "Build Your Network",
      description: "Create a powerful referral network and earn passive income",
      color: "#1E40AF",
    },
    {
      icon: <LocalOffer sx={{ fontSize: 32 }} />,
      title: "Exclusive Discounts",
      description: "Use your points and cashback for discounts on future purchases",
      color: "#D4AF37",
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "24px",
          overflow: "hidden",
          background: "transparent",
          boxShadow: "0 25px 80px rgba(0, 0, 0, 0.4)",
        },
      }}
      TransitionComponent={Zoom}
      transitionDuration={400}
    >
      <Box
        sx={{
          background: `linear-gradient(135deg, 
            #0f0c29 0%, 
            #302b63 50%, 
            #24243e 100%)`,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Animated Background Elements */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflow: "hidden",
            pointerEvents: "none",
          }}
        >
          {/* Floating diamonds */}
          {[...Array(6)].map((_, i) => (
            <Diamond
              key={i}
              sx={{
                position: "absolute",
                color: "rgba(255, 215, 0, 0.15)",
                fontSize: 40 + i * 10,
                top: `${10 + i * 15}%`,
                left: `${5 + i * 18}%`,
                animation: `float ${3 + i * 0.5}s ease-in-out infinite`,
                animationDelay: `${i * 0.3}s`,
                "@keyframes float": {
                  "0%, 100%": { transform: "translateY(0px) rotate(0deg)" },
                  "50%": { transform: "translateY(-20px) rotate(10deg)" },
                },
              }}
            />
          ))}
          
          {/* Glowing orbs */}
          <Box
            sx={{
              position: "absolute",
              width: 300,
              height: 300,
              borderRadius: "50%",
              background: "radial-gradient(circle, rgba(255,215,0,0.2) 0%, transparent 70%)",
              top: -100,
              right: -100,
              animation: "pulse 4s ease-in-out infinite",
              "@keyframes pulse": {
                "0%, 100%": { transform: "scale(1)", opacity: 0.5 },
                "50%": { transform: "scale(1.2)", opacity: 0.8 },
              },
            }}
          />
          <Box
            sx={{
              position: "absolute",
              width: 200,
              height: 200,
              borderRadius: "50%",
              background: "radial-gradient(circle, rgba(147,112,219,0.3) 0%, transparent 70%)",
              bottom: -50,
              left: -50,
              animation: "pulse 5s ease-in-out infinite",
            }}
          />
        </Box>

        {/* Close Button */}
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            color: "rgba(255, 255, 255, 0.7)",
            bgcolor: "rgba(255, 255, 255, 0.1)",
            backdropFilter: "blur(10px)",
            zIndex: 10,
            "&:hover": {
              bgcolor: "rgba(255, 255, 255, 0.2)",
              color: "white",
            },
          }}
        >
          <Close />
        </IconButton>

        <DialogContent sx={{ p: 0 }}>
          {/* Header Section */}
          <Box
            sx={{
              textAlign: "center",
              pt: 5,
              pb: 3,
              px: 3,
              position: "relative",
              zIndex: 1,
            }}
          >
            <Fade in={showContent} timeout={600}>
              <Box>
                {/* Crown/Badge Icon */}
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 90,
                    height: 90,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #ffd700 0%, #ffb347 100%)",
                    boxShadow: "0 10px 40px rgba(255, 215, 0, 0.4)",
                    mb: 3,
                    animation: "glow 2s ease-in-out infinite alternate",
                    "@keyframes glow": {
                      "0%": { boxShadow: "0 10px 40px rgba(255, 215, 0, 0.4)" },
                      "100%": { boxShadow: "0 15px 60px rgba(255, 215, 0, 0.6)" },
                    },
                  }}
                >
                  <EmojiEvents sx={{ fontSize: 50, color: "#0f0c29" }} />
                </Box>

                <Chip
                  icon={<Star sx={{ color: "#ffd700 !important" }} />}
                  label="EXCLUSIVE OFFER"
                  sx={{
                    mb: 2,
                    bgcolor: "rgba(255, 215, 0, 0.15)",
                    color: "#ffd700",
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    letterSpacing: 1,
                    border: "1px solid rgba(255, 215, 0, 0.3)",
                  }}
                />

                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 800,
                    color: "white",
                    mb: 1,
                    textShadow: "0 4px 20px rgba(0,0,0,0.3)",
                    fontSize: { xs: "1.8rem", sm: "2.5rem" },
                  }}
                >
                  Become an{" "}
                  <Box
                    component="span"
                    sx={{
                      background: "linear-gradient(135deg, #ffd700 0%, #ffb347 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    Ultra Customer
                  </Box>
                </Typography>

                <Typography
                  variant="h6"
                  sx={{
                    color: "rgba(255, 255, 255, 0.8)",
                    fontWeight: 400,
                    maxWidth: 500,
                    mx: "auto",
                    fontSize: { xs: "0.95rem", sm: "1.1rem" },
                  }}
                >
                  Unlock exclusive benefits and start earning from your referral network
                </Typography>
              </Box>
            </Fade>
          </Box>

          {/* Benefits Grid */}
          <Box sx={{ px: { xs: 2, sm: 4 }, pb: 3, position: "relative", zIndex: 1 }}>
            <Grid container spacing={2}>
              {benefits.map((benefit, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Fade in={showContent} timeout={800 + index * 150}>
                    <Box
                      sx={{
                        p: 2.5,
                        borderRadius: "16px",
                        background: "rgba(255, 255, 255, 0.08)",
                        backdropFilter: "blur(10px)",
                        border: "1px solid rgba(255, 255, 255, 0.1)",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          background: "rgba(255, 255, 255, 0.12)",
                          transform: "translateY(-4px)",
                          boxShadow: `0 10px 30px rgba(0, 0, 0, 0.3)`,
                        },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: "12px",
                            background: `${benefit.color}20`,
                            color: benefit.color,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {benefit.icon}
                        </Box>
                        <Box>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 700,
                              color: "white",
                              mb: 0.5,
                            }}
                          >
                            {benefit.title}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "rgba(255, 255, 255, 0.7)",
                              lineHeight: 1.5,
                            }}
                          >
                            {benefit.description}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Fade>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Price & CTA Section */}
          <Fade in={showContent} timeout={1200}>
            <Box
              sx={{
                mx: { xs: 2, sm: 4 },
                mb: 4,
                p: 3,
                borderRadius: "20px",
                background: "linear-gradient(135deg, rgba(255,215,0,0.15) 0%, rgba(255,179,71,0.1) 100%)",
                border: "1px solid rgba(255, 215, 0, 0.3)",
                textAlign: "center",
                position: "relative",
                zIndex: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, mb: 2 }}>
                <Verified sx={{ color: "#00BFFF", fontSize: 24 }} />
                <Typography variant="body2" sx={{ color: "#00BFFF", fontWeight: 600 }}>
                  One-time activation • Lifetime benefits
                </Typography>
              </Box>

              <Typography
                variant="h2"
                sx={{
                  fontWeight: 800,
                  color: "white",
                  mb: 1,
                  display: "flex",
                  alignItems: "baseline",
                  justifyContent: "center",
                  gap: 1,
                }}
              >
                <Box component="span" sx={{ fontSize: "1.5rem", color: "rgba(255,255,255,0.6)" }}>
                  ₹
                </Box>
                {CURRENT_ACTIVATION_FEE}
                <Box
                  component="span"
                  sx={{
                    fontSize: "1rem",
                    color: "rgba(255,255,255,0.5)",
                    fontWeight: 400,
                  }}
                >
                  activation
                </Box>
              </Typography>

              <Typography
                variant="body2"
                sx={{ color: "rgba(255, 255, 255, 0.6)", mb: 2 }}
              >
                + Get ₹{CURRENT_ACTIVATION_FEE} cashback instantly after purchase!
              </Typography>

              {/* Alternative way to become Ultra */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  mb: 3,
                  py: 1.5,
                  px: 2,
                  borderRadius: "12px",
                  background: "rgba(255, 255, 255, 0.08)",
                  border: "1px dashed rgba(255, 255, 255, 0.2)",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ 
                    color: "rgba(255, 255, 255, 0.8)",
                    textAlign: "center",
                  }}
                >
                  💡 <strong>Or</strong> purchase items worth{" "}
                  <Box 
                    component="span" 
                    sx={{ 
                      color: "#ffd700", 
                      fontWeight: 700,
                    }}
                  >
                    ₹6,999+
                  </Box>{" "}
                  to become Ultra automatically!
                </Typography>
              </Box>

              <Button
                variant="contained"
                size="large"
                onClick={onPurchase}
                startIcon={<RocketLaunch />}
                sx={{
                  py: 1.5,
                  px: 5,
                  borderRadius: "50px",
                  fontSize: "1.1rem",
                  fontWeight: 700,
                  textTransform: "none",
                  background: "linear-gradient(135deg, #ffd700 0%, #ffb347 100%)",
                  color: "#0f0c29",
                  boxShadow: "0 8px 30px rgba(255, 215, 0, 0.4)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    background: "linear-gradient(135deg, #ffb347 0%, #ffd700 100%)",
                    transform: "scale(1.05)",
                    boxShadow: "0 12px 40px rgba(255, 215, 0, 0.5)",
                  },
                }}
              >
                Become Ultra Now
              </Button>

              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 2,
                  color: "rgba(255, 255, 255, 0.5)",
                }}
              >
                Join 500+ Ultra customers earning every day
              </Typography>
            </Box>
          </Fade>

          {/* Bottom Wave Decoration */}
          <Box
            sx={{
              position: "relative",
              height: 40,
              background: "linear-gradient(180deg, transparent 0%, rgba(255,215,0,0.05) 100%)",
            }}
          />
        </DialogContent>
      </Box>
    </Dialog>
  );
};

export default UltraCustomerPopup;

